import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';

// 'users' is kept for backward compatibility but redirects to 'artists'
export type ContentView = 'users' | 'artists' | 'projects' | 'teams' | 'rates' | 'bidding' | 'finance';

interface ContentViewToggleProps {
  currentView: ContentView;
  onViewChange?: (view: ContentView) => void; // Optional for backward compatibility
  currentRole?: string | null;
}

const ContentViewToggle: React.FC<ContentViewToggleProps> = ({ currentView, onViewChange, currentRole }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const handleViewChange = (view: ContentView) => {
    // Navigate to the appropriate route
    const routes: Record<ContentView, string> = {
      'users': '/artists', // Redirect to artists
      'artists': '/artists',
      'projects': '/projects',
      'teams': '/teams',
      'rates': '/rates',
      'bidding': '/bidding',
      'finance': '/finance'
    };
    
    navigate(routes[view]);
    
    // Call callback if provided (for backward compatibility)
    if (onViewChange) {
      onViewChange(view);
    }
  };
  
  // All roles can see artist profiles (Demo with restrictions applied in ArtistCard)
  const canViewArtists = true;
  // Normalize currentView - treat 'users' as 'artists'
  const normalizedView = currentView === 'users' ? 'artists' : currentView;
  
  return (
    <div className="inline-flex bg-gray-800 rounded-lg p-1 shadow-md">
      {/* Single "Профили" (Artists) tab for all roles */}
      {canViewArtists && (
        <button
          onClick={() => handleViewChange('artists')}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-all
            ${normalizedView === 'artists'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }
          `}
          title={t('contentView.artists') || 'Профили'}
        >
          <ICONS.User className="w-4 h-4" />
          <span className="hidden sm:inline">{t('contentView.artists') || 'Профили'}</span>
        </button>
      )}
      
      <button
        onClick={() => handleViewChange('teams')}
        className={`
          flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-all
          ${currentView === 'teams'
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }
        `}
        title={t('contentView.teams') || 'Команды'}
      >
        <ICONS.Users className="w-4 h-4" />
        <span className="hidden sm:inline">{t('contentView.teams') || 'Команды'}</span>
      </button>
      
      <button
        onClick={() => handleViewChange('rates')}
        className={`
          flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-all
          ${currentView === 'rates'
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }
        `}
        title={t('contentView.rates') || 'Ставки'}
      >
        <ICONS.Calculator className="w-4 h-4" />
        <span className="hidden sm:inline">{t('contentView.rates') || 'Ставки'}</span>
      </button>
      
      <button
        onClick={() => handleViewChange('projects')}
        className={`
          flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-all
          ${currentView === 'projects'
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }
        `}
        title={t('contentView.projects')}
      >
        <ICONS.Briefcase className="w-4 h-4" />
        <span className="hidden sm:inline">{t('contentView.projects')}</span>
      </button>

      <button
        onClick={() => handleViewChange('bidding')}
        className={`
          flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-all
          ${currentView === 'bidding'
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }
        `}
        title={t('contentView.bidding')}
      >
        <ICONS.List className="w-4 h-4" />
        <span className="hidden sm:inline">{t('contentView.bidding')}</span>
      </button>

      <button
        onClick={() => handleViewChange('finance')}
        className={`
          flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-all
          ${currentView === 'finance'
            ? 'bg-indigo-600 text-white shadow-sm'
            : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }
        `}
        title={t('contentView.finance')}
      >
        <ICONS.CreditCard className="w-4 h-4" />
        <span className="hidden sm:inline">{t('contentView.finance')}</span>
      </button>
    </div>
  );
};

export default ContentViewToggle;

